import angular from 'angular';
import csmCommonWeb from 'csm-common-web';
import genderSelect from '../../../ui/genderSelect/genderSelect';
import booksSvc from '../../../util/BooksSvc/BooksSvc';

import entityContentTemplate from './addChangeEntityContent.template.html';

export default angular
  .module('books.dialogs.entities.addChangeEntityDialog', [
    csmCommonWeb,
    genderSelect,
    booksSvc
  ])
  .directive('addChangeEntityContent', addChangeEntityContentDirective)
  .service('AddChangeEntityDialog', addChangeEntityDialogService).name;

/*******************************************************************************
 * addChangeEntityContentDirective */
/**
 * Directive: addChangeEntityContent. Provides content for managing common entity
 * information. During the linking phase, an entity record is loaded from the
 * server or a new entity object is created. Uses transclusion to incorporate
 * additional input fields for the name/main tab and the additional tab content.
 * Attributes: additionalGroupLabel, additionalValidation, entitySet, entityType,
 * excludeFax, excludeFullName, excludeGender, excludeNotes, excludeWebsite,
 * fullNameLabel, id (0=new), loadService, mainValidation, moveNotes, onCancel,
 * onSave, phone2Label, requireAddress, requireEmail, requireNames and
 * saveService.
 *******************************************************************************/
/* @ngInject */ function addChangeEntityContentDirective(
  Alert,
  BooksSvc,
  SERVER
) {
  /** RegExp: "LastName, FirstName MiddleName". @private */
  var RE_NAME1 = /^([^\s,]+)\s*(?:,\s*(\S+)\s*(\S+)?)?$/;

  /** RegExp: "FirstName MiddleName LastName". @private */
  var RE_NAME2 = /^(\S+)\s*(\S+)?\s*(\S+)?$/;

  /** Type Configurations. */
  var TYPES_CONFIG = [
    { type: SERVER.BooksDBConstants.ET_OTHER, label: 'Name', names: false },
    { type: SERVER.BooksDBConstants.ET_PARENT, label: 'Parent', names: true },
    {
      type: SERVER.BooksDBConstants.ET_GRANDPARENT,
      label: 'Grandparent',
      names: true
    },
    {
      type: SERVER.BooksDBConstants.ET_COMMUNITY_PARTNER,
      label: 'Community Partner',
      names: false
    },
    {
      type: SERVER.BooksDBConstants.ET_STUDENT,
      label: 'Student',
      names: true
    },
    {
      type: SERVER.BooksDBConstants.ET_TEACHER,
      label: 'Teacher',
      names: true
    },
    {
      type: SERVER.BooksDBConstants.ET_VENDOR,
      label: 'Vendor',
      names: false
    },
    { type: SERVER.BooksDBConstants.ET_FAMILY, label: 'Family', names: true }
  ];

  /*-----------------------------*/
  /* Directive Definition Object */
  /*-----------------------------*/
  return {
    restrict: 'E',
    scope: {
      additionalGroupLabel: '@',
      additionalValidation: '&',
      entitySet: '&',
      entityType: '@',
      fullNameLabel: '@',
      id: '@entityId',
      loadService: '&',
      mainValidation: '&',
      onCancel: '&',
      onSave: '&',
      phone2Label: '@',
      saveService: '<'
    },
    template: entityContentTemplate,
    transclude: { main: '?mainContent', additional: '?additionalContent' },
    link: function (scope, elem, attr, ctrl, $transclude) {
      /*---------------------------------------------------*/
      /* Verify that a valid entity type has been provided */
      /* by grabbing the corresponding config object       */
      /*---------------------------------------------------*/
      scope.typeConfig = TYPES_CONFIG.filter(function (c) {
        return c.type == scope.entityType;
      }).shift();
      if (!scope.typeConfig) throw new Error('Invalid entity type');

      /** Name Part Max Characters */
      scope.MAX_PART_CHARS = 50;

      /** Notes Max Characters */
      scope.MAX_NOTES_CHARS = 500;

      /** Name Validator Control Object. */
      scope.valNames = {};

      /** Address Validator Control Object. */
      scope.valAddress = {};

      /** List of flag attributes to observe. */
      var mFlagAttributes = [
        'excludeFax',
        'excludeFullName',
        'excludeGender',
        'excludeNotes',
        'excludeWebsite',
        'moveNotes',
        'readOnly',
        'requireAddress',
        'requireEmail',
        'requireNames'
      ];

      /** Active Tab Indicators corresponding to each tab. */
      scope.tabActiveStatus = [true, false, false];

      /*************************************************************************
       * cancel */
      /**
       * Execute the provided onCancel logic
       *************************************************************************/
      scope.cancel = function () {
        scope.onCancel();
      };

      /** Accessor for the Full Name Label. Defaults to Full Name. */
      scope.getFullNameLabel = function () {
        return scope.fullNameLabel || 'Full Name';
      };

      /** Returns an indication that the additional content tab should be shown when
          there is content or the notes have been moved */
      scope.hasAdditionalContent = function () {
        return (
          $transclude.isSlotFilled('additional') ||
          (scope.moveNotes && !scope.excludeNotes)
        );
      };

      /** New entity indicated by a non-valid number. */
      scope.isNew = function () {
        return !Number(scope.id);
      };

      /** Flag to indicate first/last names are required based on type config or requireNames property. */
      scope.namesRequired = function () {
        return scope.requireNames || scope.typeConfig.names;
      };

      /*************************************************************************
       * save */
      /**
       * Performs validation for each tab. Switching to first tab with invalid
       * content. If everything is valid, a save is performed using the saveService
       * or BooksSvc.saveEntity. The onSave expression is evaluated upon response
       * with a results object consisting of the following:
       *   added      - New entity added
       *   entity     - Entity object saved
       *   entityType - Saved entity type
       *************************************************************************/
      scope.save = function () {
        /*--------------------------------*/
        /* Validate the first tab content */
        /*--------------------------------*/
        var namesValid = scope.valNames.validate();
        var mainContentValid = scope.mainValidation() != false;

        if (!namesValid || !mainContentValid) {
          scope.tabActiveStatus[0] = true;
          return;
        }

        /*------------------------------*/
        /* Validate the address content */
        /*------------------------------*/
        if (!scope.valAddress.validate()) {
          scope.tabActiveStatus[1] = true;
          return;
        }

        /*--------------------------------------*/
        /* Validate the additional info content */
        /*--------------------------------------*/
        if (
          scope.hasAdditionalContent() &&
          scope.additionalValidation() == false
        ) {
          scope.tabActiveStatus[2] = true;
          return;
        }

        /*------------------------------------------------*/
        /* Data is valid, perform save using the provided */
        /* saveService or the standard save method        */
        /*------------------------------------------------*/
        var promise = scope.saveService
          ? scope.saveService(scope.entity)
          : BooksSvc.saveEntity(scope.entityType, scope.entity);
        promise.then(
          function (resp) {
            scope.entity.id = Number(resp.data);
            scope.onSave({
              results: {
                added: scope.isNew(),
                entity: scope.entity,
                entityType: scope.entityType
              }
            });
            Alert.success(
              `${scope.typeConfig.label} information saved successfully.`
            );
          },
          function (resp) {
            Alert.error(resp.parsedResponse.message());
          }
        );
      };

      /*************************************************************************
       * setName */
      /**
       * Parse out the name parts from the entity name if no name parts are
       * currently set and the names are required.
       *
       * @param name  Optional name to set on the entity
       *************************************************************************/
      scope.setName = function (name) {
        if (name) scope.entity.name = name;

        if (scope.namesRequired() && !scope.excludeFullName) {
          var trimmedName = (scope.entity.name || '').trim();
          if (
            trimmedName.length > 0 &&
            (scope.entity.firstName || '').length == 0 &&
            (scope.entity.middleName || '').length == 0 &&
            (scope.entity.lastName || '').length == 0
          ) {
            /*-------------------------------------------------------*/
            /* Try both formats of input to prepopulate name fields. */
            /*-------------------------------------------------------*/
            var ra = RE_NAME1.exec(trimmedName);

            if (ra) {
              scope.entity.lastName = ra[1];
              scope.entity.firstName = ra[2];
              scope.entity.middleName = ra[3];
            } else if ((ra = RE_NAME2.exec(trimmedName))) {
              scope.entity.firstName = ra[1];
              scope.entity.middleName = ra[3] ? ra[2] : null;
              scope.entity.lastName = ra[3] ? ra[3] : ra[2];
            }
          }
        }
      };

      /** Returns the dialog header text. */
      scope.header = function () {
        if (scope.readOnly)
          return 'View {0} Information'.format(scope.typeConfig.label);

        return (
          scope.isNew() ? 'Add New {0}' : 'Update {0} Information'
        ).format(scope.typeConfig.label);
      };

      /*--------------------------------------*/
      /* Set attribute observations for the   */
      /* flag attributes and default to false */
      /*--------------------------------------*/
      mFlagAttributes.forEach(function (a) {
        scope[a] = false;
        attr.$observe(a, function () {
          scope[a] = attr[a] == 'true';
        });
      });

      /*-----------------------------*/
      /* Initialize the entity model */
      /*-----------------------------*/
      if (scope.isNew()) {
        scope.entity = {
          active: true,
          type: scope.entityType,
          addressInfo: {}
        };

        if (attr.nameInput) scope.setName(attr.nameInput);

        scope.entitySet({ entityModel: scope.entity });
      } else {
        /*---------------------------------------------*/
        /* Load the entity information from the server */
        /*---------------------------------------------*/
        (scope.loadService() || BooksSvc.getEntity(scope.id)).then(
          function (resp) {
            scope.entity = resp.data;

            /*---------------------------------------*/
            /* Ensure an address object is available */
            /*---------------------------------------*/
            if (!scope.entity.addressInfo) scope.entity.addressInfo = {};

            scope.entitySet({ entityModel: scope.entity });
          }
        );
      }

      /*----------------------------------------------*/
      /* Gender, as well as Grandparent and Community */
      /* Partner types are only available to PTA.     */
      /*----------------------------------------------*/
      if (!SERVER.currentUser.orgType.startsWith('PTA'))
        scope.excludeGender = true;
    }
  };
}

/*******************************************************************************
 * Service: AddChangeEntityDialog
 *
 * This returns an object that will display a modal dialog for managing an
 * entity's information.
 *******************************************************************************/
/* @ngInject */
function addChangeEntityDialogService($uibModal) {
  /*****************************************************************************
   * show */
  /**
   * Open a modal dialog to manage/create an entity object.
   *
   * @param  id            Entity ID to load. 0=New
   * @param  entityType    Entity type to manage
   * @param  nameInput     Initial name to set for new vendors
   * @param  saveService   Function to be passed in to the AddChangeEntityContent,
   *                       to be called on save.
   * @return               Promise object to listen for save/cancel
   *****************************************************************************/
  this.show = function (id, entityType, nameInput, saveService) {
    return $uibModal.open({
      backdrop: 'static',
      controller: function ($scope) {
        $scope.id = id;
        $scope.saveService = saveService;
        $scope.entityType = entityType;
        $scope.nameInput = nameInput;
      },
      template:
        '<add-change-entity-content entity-type="{{entityType}}" entity-id="{{id}}" name-input="{{nameInput}}" on-cancel="$dismiss()" on-save="$close(results)" save-service="saveService" ></add-change-entity-content>'
    }).result;
  };
}
